import { useState } from 'react';
import { useSearchParams } from 'react-router';
import type { MealOutput } from '@st/contract';
import { useNutritionDay, useNutritionHistory, useRemoveMeal } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { addDays, formatDate, formatNumber, todayIso } from '../../lib/format';
import { MEAL_SLOT_KEY } from '../../lib/nutrition';
import { MealModal } from './MealModal';
import {
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  MealCard,
  Modal,
  SkeletonCard,
  SkeletonList,
  StatTile,
  useToast,
} from '../../ui';

/**
 * Dnevnik prehrane nad `/v1/nutrition/*`.
 *
 * Ruta vraća obroke po terminu (`slot`), opis, više fotografija i komentar
 * trenera. Ciljne vrijednosti (`goals`, `waterGoalMl`) ne postoje ni u modelu
 * podataka ni na API-ju — portal ih je imao samo u mocku, pa je ekran u demou
 * crtao traku napretka prema izmišljenom cilju. Do trenera koji cilj upiše,
 * prikazuju se stvarni zbrojevi.
 *
 * Unos i brisanje obroka idu kroz `nutrition.addMeal` i `nutrition.removeMeal`
 * iz `@st/contract`. Ranije ih ugovor nije izlagao, pa je ekran nudio gumb koji
 * nije radio ništa.
 */
const DAN = /^\d{4}-\d{2}-\d{2}$/;

export function Nutrition() {
  const { t, locale } = useTranslation();
  /*
    Dan može doći iz adrese (`/prehrana?dan=2026-03-05`). Obavijest o komentaru
    trenera vodi upravo ovamo, pa bez čitanja parametra klijent sleti na
    današnji dan i ne nađe obrok o kojem je poruka.
  */
  const [params, setParams] = useSearchParams();
  const izAdrese = params.get('dan');
  const [day, setDayState] = useState(
    izAdrese && DAN.test(izAdrese) ? izAdrese : todayIso(),
  );

  function setDay(next: string | ((prev: string) => string)): void {
    setDayState((prev) => {
      const vrijednost = typeof next === 'function' ? next(prev) : next;
      // Adresa prati ekran, pa se dan može podijeliti i preživi osvježavanje.
      setParams(
        (p) => {
          const kopija = new URLSearchParams(p);
          if (vrijednost === todayIso()) kopija.delete('dan');
          else kopija.set('dan', vrijednost);
          return kopija;
        },
        { replace: true },
      );
      return vrijednost;
    });
  }
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<MealOutput | null>(null);
  /* Sličica je 80 px — dovoljna da se vidi da slika postoji, premala da se vidi obrok. */
  const [slika, setSlika] = useState<{ url: string; naziv: string } | null>(null);

  const query = useNutritionDay(day);
  const history = useNutritionHistory();
  const removeMeal = useRemoveMeal();
  const { toast } = useToast();

  /** Obrok se u potvrdi brisanja imenuje, nikad „jesi li sigurna?". */
  function mealLabel(meal: MealOutput): string {
    const slot = t(MEAL_SLOT_KEY[meal.slot]);
    return meal.description ? `${slot} — ${meal.description}` : slot;
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMeal.mutateAsync({ id: pendingDelete.id, day });
      setPendingDelete(null);
      toast(t('nutrition.mealDeleted'), 'success');
    } catch (err) {
      setPendingDelete(null);
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <>
      <PageHeader
        title={t('nutrition.title')}
        subtitle={formatDate(`${day}T00:00:00.000Z`, locale)}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDay((d) => addDays(d, -1))}>
              ← {t('nutrition.prevDay')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={day >= todayIso()}
              onClick={() => setDay((d) => addDays(d, 1))}
            >
              {t('nutrition.nextDay')} →
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              {t('nutrition.addMeal')}
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-6">
        <QueryBoundary
          query={query}
          skeleton={<SkeletonList rows={3} />}
          isEmpty={(d) => d.meals.length === 0}
          empty={{
            message: t('empty.noMealsPortal'),
            actionLabel: t('empty.addFirstMeal'),
            onAction: () => setAddOpen(true),
          }}
        >
          {(data) => (
            <div className="flex flex-col gap-5">
              <Card>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatTile
                    label={t('nutrition.kcal')}
                    value={formatNumber(data.totals.kcal, locale)}
                    unit="kcal"
                  />
                  <StatTile
                    label={t('nutrition.protein')}
                    value={formatNumber(data.totals.proteinG, locale)}
                    unit="g"
                  />
                  <StatTile
                    label={t('nutrition.carbs')}
                    value={formatNumber(data.totals.carbsG, locale)}
                    unit="g"
                  />
                  <StatTile
                    label={t('nutrition.fat')}
                    value={formatNumber(data.totals.fatG, locale)}
                    unit="g"
                  />
                </div>
                <div className="mt-3">
                  <StatTile
                    label={t('nutrition.water')}
                    value={formatNumber(data.waterMl / 1000, locale, 1)}
                    unit="l"
                  />
                </div>
              </Card>

              <section>
                <h2 className="eyebrow mb-3">{t('nutrition.meals')}</h2>
                <ul className="flex flex-col gap-3">
                  {data.meals.map((m) => (
                    <li key={m.id}>
                      <MealCard
                        name={t(MEAL_SLOT_KEY[m.slot])}
                        timeLabel={formatDate(`${m.eatenOn}T00:00:00.000Z`, locale)}
                        note={m.description}
                        photoUrl={m.photoUrls[0] ?? null}
                        onPhotoOpen={
                          m.photoUrls[0]
                            ? () =>
                                setSlika({
                                  url: m.photoUrls[0] as string,
                                  naziv: t(MEAL_SLOT_KEY[m.slot]),
                                })
                            : undefined
                        }
                        photoOpenLabel={t('nutrition.photoOpen')}
                        trainerComment={m.trainerComment}
                        trainerCommentLabel={t('nutrition.trainerComment')}
                        macros={{
                          kcal: m.kcal ?? 0,
                          proteinG: m.proteinG ?? 0,
                          carbsG: m.carbsG ?? 0,
                          fatG: m.fatG ?? 0,
                        }}
                        labels={{
                          kcal: 'kcal',
                          protein: t('nutrition.protein'),
                          carbs: t('nutrition.carbs'),
                          fat: t('nutrition.fat'),
                        }}
                        action={
                          /* Ime obroka je u pristupačnom nazivu — inače je na ekranu
                             pet gumba koji se čitaču zaslona svi zovu „Obriši". */
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('nutrition.deleteMealOf', { meal: mealLabel(m) })}
                            onClick={() => setPendingDelete(m)}
                          >
                            {t('action.delete')}
                          </Button>
                        }
                      />
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </QueryBoundary>

        {/*
          Dnevnik unatrag. Bez njega se do ranijeg dana dolazi samo klikanjem na
          „Prethodni dan", pa je povijest praktički nevidljiva.
        */}
        <Card>
          <CardHeader title={t('nutrition.history')} />
          <QueryBoundary
            query={history}
            skeleton={<SkeletonCard />}
            isEmpty={(d) => d.days.length === 0}
            empty={{ message: t('empty.noHistory') }}
          >
            {(data) => (
              <ul className="flex flex-col divide-y divide-line-subtle">
                {data.days.map((entry) => (
                  <li key={entry.day}>
                    <button
                      type="button"
                      onClick={() => setDay(entry.day)}
                      aria-current={entry.day === day ? 'true' : undefined}
                      className={
                        'flex w-full items-baseline justify-between gap-3 py-2.5 text-left ' +
                        'transition-colors hover:text-accent-dark aria-[current]:text-accent-dark'
                      }
                    >
                      <span className="text-[14.5px] text-ink">
                        {formatDate(`${entry.day}T00:00:00.000Z`, locale)}
                      </span>
                      <span className="text-[13px] text-ink-muted">
                        {t('nutrition.mealsCount', { n: entry.count })} ·{' '}
                        {formatNumber(entry.kcal, locale)} kcal
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </QueryBoundary>
        </Card>
      </div>

      {slika && (
        <Modal
          open
          onClose={() => setSlika(null)}
          title={slika.naziv}
          closeLabel={t('common.close')}
        >
          <img
            src={slika.url}
            alt={t('nutrition.mealPhoto')}
            className="mx-auto max-h-[70vh] w-auto max-w-full rounded-md"
          />
        </Modal>
      )}

      <MealModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        day={day}
        onSaved={() => {
          setAddOpen(false);
          toast(t('nutrition.mealSaved'), 'success');
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('nutrition.deleteMeal')}
        question={
          pendingDelete ? t('nutrition.deleteMealQuestion', { meal: mealLabel(pendingDelete) }) : ''
        }
        confirmLabel={t('action.confirmDelete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={removeMeal.isPending}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
      />
    </>
  );
}
